import type { TabView } from "@e308/ux";
import type { SeatObservation } from "../types.js";
import type { AppIntent, AppState } from "./app-source.js";
import { initialMilitaryDraft } from "./app-state-utils.js";
import { borderOrders } from "./border-orders-view.js";
import {
  productionRouting,
  reinforcementControls,
  routingSliders,
} from "./border-reinforcement-view.js";
import { borderSituation, compactTroops, frontHeadline, seatName } from "./border-situation.js";
import { incomingTroops, manualTransfers, transitView } from "./border-transfers-view.js";
import { allocationFit } from "./economy-view.js";
import { undefendedAttacks } from "./guidance.js";
import { help } from "./layout-view.js";
import {
  formatGameClock,
  gameIntent,
  heading,
  type AppNode as Node,
  name,
  text,
} from "./view-nodes.js";

export function bordersTab(state: AppState): TabView<AppIntent, string> {
  const o = state.observation as SeatObservation;
  return {
    id: "borders",
    label: "Borders",
    content: [
      heading("borders-heading", "Borders"),
      ...closedBorderNotices(o),
      productionRouting(o, state.armyShares),
      help(
        "blast-doors",
        "HQ protection",
        o.self.blastDoorsActive
          ? "Blast Doors active: Headquarters damage is reduced while at least one Shield Generator operates."
          : "Blast Doors inactive: operate a Shield Generator to protect Headquarters after a breach.",
      ),
      help(
        "unit-help",
        "Choose your army",
        "Guard Robots are efficient against troops. Raider Robots can split fire during Raid. Siege Guns excel against exposed infrastructure but are weak against troops and use three Supply each. Robot Weapons and Reinforced Robots improve Guards and Raiders, not Siege Guns. Siege Shells improves Siege Gun shield/building damage. All units cost Metal to build; none steals Metal.",
      ),
      {
        kind: "stack",
        id: "border-cards",
        style: { className: "kf-border-cards" },
        children: Object.keys(o.self.logistics.borders).map((id) => borderCard(state, id)),
      },
      transitView(o),
      gameForecast(state),
    ],
  };
}

function closedBorderNotices(o: SeatObservation): Node[] {
  const fallen = o.match.eliminated.filter((entry) => entry.seatId !== o.self.id);
  if (!fallen.length) return [];
  return [
    {
      kind: "stack",
      id: "closed-borders",
      style: { className: "kf-closed-borders" },
      children: [...fallen].reverse().map((entry) => ({
        kind: "notification",
        id: `closed-border-${entry.seatId}`,
        tone: "neutral",
        text: `${seatName(o, entry.seatId)} defeated — borders closed · ${formatGameClock(entry.atGameMs)} game time.`,
      })),
    },
  ];
}

function borderCard(state: AppState, id: string): Node {
  const o = state.observation as SeatObservation;
  const draft = state.militaryDraft[id] ?? initialMilitaryDraft;
  return {
    kind: "section",
    id: `front-${id}`,
    variant: "card",
    style: {
      className: `kf-border-card${undefendedAttacks(o).some((warning) => warning.borderId === id) ? " kf-front-danger" : ""}`,
    },
    children: [
      heading(`front-heading-${id}`, `${seatName(o, id)} border`, 3),
      text(`situation-${id}`, frontHeadline(o, id)),
      text(
        `your-order-${id}`,
        `Your order: ${name(o.self.logistics.borders[id]?.standingOrder ?? "hold")}`,
      ),
      ...forces(o, id).slice(0, 2),
      text(`incoming-${id}`, `Your reinforcements: ${incomingTroops(o, id)}`),
      routingSliders(o, id, state.armyShares),
      reinforcementControls(state, id),
      borderOrders(o, id),
      manualTransfers(o, id, draft),
      allocationControls(o, id),
      {
        kind: "infobox",
        id: `battle-details-${id}`,
        title: "Battle details",
        initiallyOpen: false,
        content: [
          text(`full-situation-${id}`, borderSituation(o, id)),
          ...forces(o, id).slice(2),
          ...attackReport(o, id),
        ],
      },
    ],
  };
}

function forces(o: SeatObservation, id: string): Node[] {
  const ours = o.self.logistics.borders[id];
  const enemy = o.seats.find((seat) => seat.id === id);
  const theirs = enemy?.logistics.borders[o.self.id];
  const supply = o.self.borderSupply[id];
  if (!ours || !theirs || !supply) return [];
  return [
    text(`our-troops-${id}`, `You: ${compactTroops(ours.troops)}`),
    text(`enemy-troops-${id}`, `Them: ${compactTroops(theirs.troops)}`),
    text(
      `supply-summary-${id}`,
      `Your Supply: ${supply.received} available / ${supply.required} required · firing effectiveness ${supply.effectiveness} (1 = fully supplied).`,
    ),
    text(
      `shield-summary-${id}`,
      `Shield charge: yours ${o.self.combat.borders[id]?.shieldCharge ?? "0"} · theirs ${enemy?.combat.borders[o.self.id]?.shieldCharge ?? "0"}.`,
    ),
  ];
}

function attackReport(o: SeatObservation, id: string): Node[] {
  const attack = o.self.combat.borders[id]?.lastAttack;
  return attack
    ? [
        {
          kind: "infobox",
          id: `report-${id}`,
          title: "Last outgoing volley",
          initiallyOpen: false,
          content: [
            text(
              `attack-${id}`,
              `Last attack: ${attack.infrastructureDamage} infrastructure damage to ${name(attack.effectiveTarget)}, ${attack.troopDamage} troop damage, ${attack.shieldAbsorbed} absorbed by shields. Blast Doors ${attack.blastDoorsActive ? "applied" : "did not apply"}.`,
            ),
          ],
        },
      ]
    : [];
}

function gameForecast(state: AppState): Node {
  const forecast = state.combatForecast;
  return {
    kind: "infobox",
    id: "combat-forecast",
    title: "60-second clone forecast",
    initiallyOpen: true,
    content: [
      {
        kind: "action",
        id: "request-combat-forecast",
        action: {
          id: "request-combat-forecast",
          label: "Run conditional forecast",
          enabled: true,
          intent: { kind: "request-combat-forecast" },
          blockers: [],
        },
      },
      ...(forecast
        ? [
            text("forecast-assumptions", forecast.assumptions.join(" ")),
            text(
              "forecast-base-time",
              `Calculated from game second ${Math.floor(forecast.baseGameTimeMs / 1_000)}.`,
            ),
            ...forecast.borders.map((border) =>
              text(
                `forecast-${border.borderId}`,
                `${seatName(state.observation as SeatObservation, border.borderId)}: ${breachText(border.shieldBreachAtMs, forecast.baseGameTimeMs)} After ${forecast.horizonMs / 1_000} game seconds: shield ${border.finalShieldCharge}, effective target ${name(border.finalEffectiveTarget)}, Headquarters ${border.finalHeadquartersIntegrity}.`,
              ),
            ),
          ]
        : [
            text(
              "forecast-empty",
              "Run the authoritative cloned simulation to preview current orders without changing this match.",
            ),
          ]),
    ],
  };
}

function breachText(atMs: number | null, baseMs: number): string {
  if (atMs === null) return "No breach at current rates.";
  if (atMs <= baseMs) return "Shield already down.";
  return `Shield breach in ${Math.ceil((atMs - baseMs) / 1_000)} game seconds.`;
}

function allocationControls(o: SeatObservation, id: string): Node {
  return {
    kind: "infobox",
    id: `allocation-${id}`,
    title: `Supply & shields · ${seatName(o, id)}`,
    initiallyOpen: false,
    content: [
      text(`allocation-note-${id}`, "Divide shared town capacity between borders."),
      ...(["supply", "shield"] as const).map((kind) => {
        const current = kind === "supply" ? o.self.supplyAllocation : o.self.shieldAllocation;
        return {
          kind: "range-input" as const,
          id: `${kind}-${id}`,
          label: `${name(kind)} share for ${seatName(o, id)}`,
          value: current[id] ?? 0,
          min: 0,
          max: 100,
          step: 5,
          showTicks: true,
          intent: (value: number) =>
            gameIntent(`${kind}-${id}`, {
              kind: kind === "supply" ? "set-supply" : "set-shields",
              allocations: allocationFit(current, id, value),
            }),
        };
      }),
    ],
  };
}
